// Income and savings for each year between now and retirement, in today's
// dollars. One schedule feeds the growth simulation, the retirement plan and
// the charts, so they can never disagree about what is being saved.

import { estimateMonthlyBenefit } from './socialSecurity';

const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const num = (v, fallback = 0) => {
  const n = Number(v);
  return v != null && v !== '' && Number.isFinite(n) ? n : fallback;
};

export const SAVINGS_MODES = ['amount', 'percent'];
export const DEFAULT_INCOME_GROWTH_UNTIL = 55;

/**
 * @returns {{ age, income, ownSavings, match, total }[]} one row per year from
 *   `currentAge` up to (not including) `retirementAge`.
 *
 * Income is today's pay, or the starting pay for someone not yet working. It
 * rises by `incomeGrowthRate` a year above inflation until
 * `incomeGrowthUntilAge`, then holds its buying power.
 *
 * Savings are either a share of that income ('percent') or a dollar amount
 * that rises by `savingsGrowthRate` a year ('amount'). Nothing is saved
 * before work starts. `delayYears` pushes the first saving year back, to
 * show what waiting costs.
 */
export function buildSavingsPlan(profile, { currentAge, retirementAge, delayYears = 0 }) {
  const startWork = Math.max(currentAge, Math.round(num(profile.workStartAge, currentAge)));
  const basePay = Math.max(0, num(profile.income));
  const growth = clamp(num(profile.incomeGrowthRate), -10, 20) / 100;
  const growthUntil = Math.round(num(profile.incomeGrowthUntilAge, DEFAULT_INCOME_GROWTH_UNTIL));
  const mode = profile.savingsMode === 'percent' ? 'percent' : 'amount';
  const rate = clamp(num(profile.savingsRate), 0, 100) / 100;
  const amount = Math.max(0, num(profile.annualSavings));
  const amountGrowth = clamp(num(profile.savingsGrowthRate), -20, 20) / 100;
  const matchedShare = clamp(num(profile.matchPctOfSalary), 0, 100) / 100;
  const matchRate = clamp(num(profile.matchRate), 0, 200) / 100;
  const firstSavingAge = startWork + Math.max(0, Math.round(delayYears));
  // A spouse's pay, which stops at their own retirement age.
  const married = profile.filingStatus === 'married_filing_jointly' || profile.filingStatus === 'married_filing_separately';
  const spousePay = married ? Math.max(0, num(profile.spouse2?.income)) : 0;
  const spouseAgeNow = Math.round(num(profile.spouse2?.currentAge, currentAge));
  const spouseRetires = Math.round(num(profile.spouse2?.retirementAge, 65));

  const rows = [];
  for (let age = currentAge; age < retirementAge; age++) {
    const working = age >= startWork;
    // Raises compound from the first working year until the age they stop.
    const raises = Math.max(0, Math.min(age, growthUntil) - startWork);
    const ownIncome = working ? basePay * Math.pow(1 + growth, raises) : 0;
    const spouseAge = spouseAgeNow + (age - currentAge);
    const spouseIncome = spousePay > 0 && spouseAge < spouseRetires
      ? spousePay * Math.pow(1 + growth, Math.max(0, Math.min(spouseAge, growthUntil) - spouseAgeNow))
      : 0;
    const income = ownIncome + spouseIncome;
    let ownSavings = 0;
    if (age >= firstSavingAge) {
      ownSavings = mode === 'percent' ? income * rate : amount * Math.pow(1 + amountGrowth, age - startWork);
    }
    // The employer matches what is actually saved, up to a share of the
    // person's own pay (a spouse's pay is not matched by this employer).
    const match = Math.min(ownIncome * matchedShare, ownSavings) * matchRate;
    rows.push({ age, income, ownIncome, spouseIncome, ownSavings, match, total: ownSavings + match });
  }
  return rows;
}

/** Totals a reader can check a schedule against. */
export function summariseSavingsPlan(rows) {
  const saving = rows.filter((row) => row.total > 0);
  const peak = rows.reduce((top, row) => (row.income > top.income ? row : top), { income: 0, age: null });
  return {
    firstSavingAge: saving.length ? saving[0].age : null,
    firstYearTotal: saving.length ? saving[0].total : 0,
    lastYearTotal: saving.length ? saving[saving.length - 1].total : 0,
    lifetimeOwn: rows.reduce((sum, row) => sum + row.ownSavings, 0),
    lifetimeMatch: rows.reduce((sum, row) => sum + row.match, 0),
    peakIncome: peak.income,
    peakIncomeAge: peak.age,
  };
}

// Age a career is taken to have started when it began before today.
export const ASSUMED_CAREER_START_AGE = 22;

/**
 * A rough Social Security benefit at full retirement age, in today's dollars,
 * from the pay path in the profile: the years already worked (pay assumed to
 * have risen to today's level at the same rate of raises) and the years still
 * to work until retirement. Rounded to the nearest $50 a month. An estimate to
 * start from; the statement at ssa.gov uses the actual earnings record.
 */
export function estimateSocialSecurityFromPay(profile, { currentAge, retirementAge }) {
  const future = buildSavingsPlan(profile, { currentAge, retirementAge }).map((row) => row.ownIncome);
  const pay = Math.max(0, num(profile.income));
  const growth = clamp(num(profile.incomeGrowthRate), -10, 20) / 100;
  const growthUntil = Math.round(num(profile.incomeGrowthUntilAge, DEFAULT_INCOME_GROWTH_UNTIL));
  const working = !(num(profile.workStartAge, currentAge) > currentAge);
  const past = [];
  if (working) {
    for (let age = ASSUMED_CAREER_START_AGE; age < currentAge; age++) {
      // Raises that happened between that age and now.
      const raises = Math.max(0, Math.min(currentAge, growthUntil) - Math.min(age, growthUntil));
      past.push(pay / Math.pow(1 + growth, raises));
    }
  }
  const monthly = estimateMonthlyBenefit([...past, ...future]);
  return Math.round(monthly / 50) * 50;
}
